import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  AccountCircle,
  AddCircle,
  ArrowDropDownOutlined,
  ArrowDropUpOutlined,
  AttachFile,
  Email,
  Label,
  Place,
  RemoveCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import QES_CONSTANTS from 'generic/core/qes/constants';
import { documentPropType } from 'generic/core/qes/proptypes';
import { overrideRessource } from 'generic/utils/utils';
import { getFieldIdFromFormFields } from 'generic/utils/qesUtils';
import { format } from 'generic/utils/dateUtils';
import ExpandableTagList from 'generic/components/ui/ExpandableTagList';
import IconTypeFile from 'generic/components/ui/IconTypeFile';
import JsxJoinerWithSeparator from 'generic/components/ui/JsxJoinerWithSeparator';
import { cleanupQuickResults, refineAddFacetsValues } from 'generic/core/search/actions';
import { clearSelection } from 'generic/core/selection/actions';

const { default: useDocumentFields } = overrideRessource('core/hooks/useDocumentFields', true);
const { CLICK_ON_DOCS_TITLE_BEHAVIOR } = QES_CONSTANTS;

const Document = ({
  disableTagsRefine = false,
  displayTitle = true,
  document,
  handleGetDocumentComplete = null,
  isCompleteDisplay = false,
  patchDocumentInComplete = null,
  shrinkDocumentDisplay = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    attachementObject,
    date,
    emailAccount,
    fileHtmlUrl,
    folder,
    id,
    language,
    level,
    linkout,
    messageId,
    mime,
    receptionDate,
    scope,
    sendCc,
    sendCcEmail,
    sendDate,
    sendFrom,
    sendFromEmail,
    sendTo,
    sendToEmail,
    sentences,
    status,
    text,
    title,
    typeDocument,
    userModif,
  } = useDocumentFields(document);
  const { qesdocument } = document;

  const [seeMore, setSeeMore] = useState(false);

  let attachmentFiltered = [...attachementObject];

  if (_.isArray(attachementObject)) {
    if (attachementObject.length > 5) {
      attachmentFiltered = seeMore ? attachementObject : attachementObject.slice(0, 5);
    }
  }

  const activeBaseFields = useSelector((state) => state.config.activeBase.champs);

  const SCOPES = [
    { libelle: 'back_up', value: 'BACKUP' },
    { libelle: 'unexploited', value: 'NEXP' },
    { libelle: 'irrelevant', value: 'NP' },
    { libelle: 'other', value: 'OTHER' },
    { libelle: 'personnal', value: 'PER' },
    { libelle: 'relevant', value: 'PERT1' },
    { libelle: 'very_relevant', value: 'PERT2' },
  ];

  const handleSearchFacet = (name, value) => {
    const baseFieldId = getFieldIdFromFormFields(activeBaseFields, name);
    dispatch(cleanupQuickResults());
    dispatch(clearSelection({ quickResultsScope: true }));
    dispatch(refineAddFacetsValues([{ champ: baseFieldId, strategie: value }]));
  };

  let titleLinkProps;
  if (CLICK_ON_DOCS_TITLE_BEHAVIOR === 'newTab') {
    titleLinkProps = {
      component: Link,
      href: linkout,
      rel: 'noopener',
      target: '_blank',
      underline: 'none',
    };
  } else if (CLICK_ON_DOCS_TITLE_BEHAVIOR === 'complete') {
    titleLinkProps = {
      component: 'span',
      onClick: handleGetDocumentComplete,
    };
  }

  return (
    <Box sx={{ display: 'block', width: '100%', wordBreak: 'break-word' }}>
      {displayTitle && (
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontSize: '1.2rem',
            margin: '6px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {typeDocument === 'Attachment' ? (
            <AttachFile fontSize="small" />
          ) : (
            <Email fontSize="small" />
          )}
          <Box
            {...titleLinkProps}
            color="text.primary"
            sx={{ cursor: 'pointer' }}
          >
            {title}
          </Box>
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: '1' }}>
          <Box sx={{ lineHeight: '1.3rem' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                mb: 0.5,
              }}
            >
              <JsxJoinerWithSeparator items={[
                date && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.neutral"
                  >
                    {date}
                  </Typography>
                ),
                !shrinkDocumentDisplay && status > 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.neutral"
                  >
                    {t(`ged.document.status.${status}`)}
                  </Typography>
                ),
                !shrinkDocumentDisplay && CLICK_ON_DOCS_TITLE_BEHAVIOR === 'complete' && linkout && (
                  <Link
                    href={linkout}
                    rel="noopener"
                    target="_blank"
                    underline="none"
                  >
                    <Typography
                      component="span"
                      variant="body2"
                    >
                      {t('actions.open_in_new_tab')}
                    </Typography>
                  </Link>
                ),
                !shrinkDocumentDisplay && fileHtmlUrl && (
                  <Link
                    href={fileHtmlUrl}
                    rel="noopener"
                    target="_blank"
                    underline="none"
                  >
                    <Typography
                      component="span"
                      variant="body2"
                    >
                      HTML
                    </Typography>
                  </Link>
                ),
              ]}
              />
            </Box>
          </Box>
          {_.isArray(sentences) && (
            sentences.map((sentence) => (
              <Box
                sx={{
                  p: 1,
                  mt: 0.5,
                  mb: 1,
                  borderLeft: '5px solid #d3d3d3',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '22px',
                }}
              >
                {sentence}
              </Box>
            ))
          )}
          {!shrinkDocumentDisplay && (
            <Fragment>
              <Box sx={{ mt: 0.5 }}>
                <ExpandableTagList
                  disableRefine={disableTagsRefine}
                  qesdocument={qesdocument}
                  tags={[
                    { fieldName: 'QES_Company', icon: AccountBalance },
                    { fieldName: 'QES_Person', icon: AccountCircle },
                    { fieldName: 'QES_Location', icon: Place },
                    { fieldName: 'QES_ConceptCategorized', icon: Label },
                  ]}
                />
              </Box>
              {isCompleteDisplay && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {_.map(SCOPES, (item) => (
                    <Chip
                      key={item.libelle}
                      label={t(`document.type_scope.${item.libelle}`)}
                      size="small"
                      icon={item.value === scope ? <RemoveCircle fontSize="small" /> : <AddCircle fontSize="small" />}
                      color={item.value === scope ? 'primary' : 'default'}
                      sx={{ height: '20px', minHeight: 'initial' }}
                      onClick={() => patchDocumentInComplete({ champs: { Scope: item.value } })}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ fontSize: '10px', mt: 1 }}>
                {!isCompleteDisplay && typeDocument && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.type_document.libelle')}</Box>
                    <Box component="span">
                      {typeDocument === 'Attachment' ? (
                        t('document.type_document.attachment')
                      ) : (
                        t('document.type_document.email')
                      )}
                    </Box>
                  </Box>
                )}
                {typeDocument === 'Attachment' && level && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.email_ref')}</Box>
                    <Box component="span">{level}</Box>
                  </Box>
                )}
                {emailAccount && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.email_account')}</Box>
                    <Box component="span">{emailAccount}</Box>
                  </Box>
                )}
                {_.isArray(sendFrom) && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.from')}</Box>
                    <JsxJoinerWithSeparator
                      items={sendFrom.map((sender, index) => (
                        <Box key={sender} component="span">
                          <Box
                            component="span"
                            onClick={() => handleSearchFacet('From', sender)}
                            sx={{ color: 'primary.main', cursor: 'pointer' }}
                          >
                            {sender}
                          </Box>
                          {_.isArray(sendFromEmail) && sendFromEmail[index] !== sender && (
                            <Box
                              component="span"
                              onClick={() => handleSearchFacet('From_email', sendFromEmail[index])}
                              sx={{ color: 'primary.main', cursor: 'pointer' }}
                            >
                              &nbsp;
                              (
                              {sendFromEmail[index]}
                              )
                            </Box>
                          )}
                        </Box>
                      ))}
                      separator={(<span>, </span>)}
                    />
                  </Box>
                )}
                {_.isArray(sendTo) && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.to')}</Box>
                    <JsxJoinerWithSeparator
                      items={sendTo.map((recipient, index) => (
                        <Box key={recipient} component="span">
                          <Box
                            component="span"
                            onClick={() => handleSearchFacet('To', recipient)}
                            sx={{ color: 'primary.main', cursor: 'pointer' }}
                          >
                            {recipient}
                          </Box>
                          {_.isArray(sendToEmail) && sendToEmail[index] !== recipient && (
                            <Box
                              component="span"
                              onClick={() => handleSearchFacet('To_email', sendToEmail[index])}
                              sx={{ color: 'primary.main', cursor: 'pointer' }}
                            >
                              (
                              {sendToEmail[index]}
                              )
                            </Box>
                          )}
                        </Box>
                      ))}
                      separator={(<span>, </span>)}
                    />
                  </Box>
                )}
                {_.isArray(sendCc) && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.cc')}</Box>
                    <JsxJoinerWithSeparator
                      items={sendCc.map((cc, index) => (
                        <Box key={cc} component="span">
                          <Box
                            component="span"
                            onClick={() => handleSearchFacet('Cc', cc)}
                            sx={{ color: 'primary.main', cursor: 'pointer' }}
                          >
                            {cc}
                          </Box>
                          {_.isArray(sendCcEmail) && sendCcEmail[index] !== cc && (
                            <Box
                              onClick={() => handleSearchFacet('Cc_email', sendCcEmail[index])}
                              sx={{ color: 'primary.main', cursor: 'pointer' }}
                            >
                              {sendCcEmail[index]}
                            </Box>
                          )}
                        </Box>
                      ))}
                      separator={(<span>, </span>)}
                    />
                  </Box>
                )}
                {folder && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.folder')}</Box>
                    <Box
                      component="span"
                      onClick={() => handleSearchFacet('Path.verbatim', folder)}
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                    >
                      {folder}
                    </Box>
                  </Box>
                )}
                {id && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.id')}</Box>
                    <Box
                      component="span"
                      onClick={() => handleSearchFacet('IDQES', id)}
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                    >
                      {id}
                    </Box>
                  </Box>
                )}
                {messageId && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.message_id')}</Box>
                    <Box
                      component="span"
                      onClick={() => handleSearchFacet('Message_id', messageId)}
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                    >
                      {messageId}
                    </Box>
                  </Box>
                )}
                {language && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.language')}</Box>
                    {language}
                  </Box>
                )}
                {mime && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.mime')}</Box>
                    {mime}
                  </Box>
                )}
                {receptionDate && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.reception_date')}</Box>
                    {format(receptionDate, 'Pp')}
                  </Box>
                )}
                {sendDate && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.send_date')}</Box>
                    {format(sendDate, 'Pp')}
                  </Box>
                )}
                {userModif && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.modified_user')}</Box>
                    {userModif}
                  </Box>
                )}
                {!isCompleteDisplay && scope && (
                  <Box>
                    <Box component="span" sx={{ fontWeight: '700' }}>{t('document.scope')}</Box>
                    {scope}
                  </Box>
                )}
                {_.isArray(attachementObject) && (
                  <Box>
                    <strong>{t('document.attachment')}</strong>
                    <List>
                      {attachmentFiltered.map((item) => (
                        <ListItem key={item.libelle} disablePadding>
                          <ListItemIcon>
                            <IconTypeFile urlFile={item.libelle} />
                          </ListItemIcon>
                          <Link
                            href={item.url}
                            rel="noopener"
                            target="_blank"
                            underline="none"
                            key={item}
                          >
                            <Box component="span">
                              {item.libelle}
                            </Box>
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                    {attachementObject.length > 5 && (
                      <Chip
                        size="extraSmall"
                        sx={{
                          height: '20px',
                          mt: '0px',
                          '& .MuiChip-icon': {
                            ml: '2px',
                            mr: '-14px',
                          },
                        }}
                        onClick={() => setSeeMore(!seeMore)}
                        variant="outlined"
                        icon={seeMore ? <ArrowDropUpOutlined /> : <ArrowDropDownOutlined />}
                        title={t(`results.tags.${seeMore ? 'less' : 'more'}`)}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Fragment>
          )}
        </Box>
      </Box>
      {isCompleteDisplay && (
        <Box sx={{ my: 2, mr: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ mt: 2, mb: 2, whiteSpace: 'pre-wrap' }}>
            {text}
          </Box>
        </Box>
      )}
    </Box>
  );
};

Document.propTypes = {
  disableTagsRefine: PropTypes.bool,
  displayTitle: PropTypes.bool,
  document: documentPropType.isRequired,
  handleGetDocumentComplete: PropTypes.func,
  handleToggleAnnotatedTextDisplay: PropTypes.func,
  isAnnotatedTextDisplayed: PropTypes.bool,
  isCompleteDisplay: PropTypes.bool,
  shrinkDocumentDisplay: PropTypes.bool,
  patchDocumentInComplete: PropTypes.func,
};

export default Document;
